/**
 * O QUE A META COBRA, à parte da mensalidade da IPCOM.
 *
 * Mora aqui, e não dentro do documento, por um motivo concreto: **estes números
 * têm validade**. São definidos pela Meta, cobrados no cartão da própria
 * empresa, e mudam sem aviso.
 *
 * ⛔ NÃO AFIRME O MECANISMO. A frase antiga dizia que os valores "variam com a
 * cotação do dólar" — plausível, e não confirmado. A tabela é publicada em BRL;
 * o câmbio implícito entre ela e a versão em dólar é estável (0,3217/0,0625 e
 * 0,0350/0,0068 dão 5,147 nas duas categorias), o que SUGERE indexação, mas
 * sugerir não é saber. Explicar como o preço se forma convida o cliente a
 * recalcular por conta própria e a cobrar a diferença. Quem define é a Meta —
 * é isso que se diz, e o documento público já diz assim.
 *
 * Pelo mesmo motivo não se imprime câmbio no documento: o backend tem teste
 * reprovando "5,14", "US$" e "USD" no HTML da proposta. Texto de preço dentro de um
 * componente de 800 linhas não é atualizado por ninguém — e proposta que afirma
 * preço vencido é pior que proposta que não fala de preço nenhum.
 *
 * Ao atualizar: mexa nos valores E na `REFERENCIA`, que sai impressa no
 * documento. O cliente precisa saber de quando é o número que está lendo.
 */

/**
 * Sai impressa junto da tabela. Atualize com os valores.
 *
 * ⚠️ Diz VIGÊNCIA e DATA DE CONFERÊNCIA, não só o mês, e isso é o que torna o
 * número citável: "julho/2026" não responde se alguém olhou depois. Texto
 * alinhado ao `META_LINHA_REFERENCIA` do backend, para as duas propostas da
 * IPCOM não afirmarem origens diferentes sobre a mesma tabela.
 */
export const REFERENCIA_DOS_PRECOS =
  'tabela oficial da Meta em BRL, vigente desde 01/07/2026, conferida em 28/09/2026';

/**
 * A partir desta data a Meta passa a cobrar também a resposta na janela de 24h,
 * hoje gratuita, como mensagem de serviço na faixa da utilidade.
 *
 * ⚠️ É por isso que a primeira linha da tabela é CALCULADA, não escrita: uma
 * proposta emitida em setembro e aberta pelo cliente em outubro precisa mostrar
 * o que ele vai pagar de verdade, não o que valia no dia da emissão.
 */
export const JANELA_24H_PASSA_A_COBRAR_EM = '2026-10-01';

/**
 * O valor cru e o RÓTULO que vai ao papel, juntos.
 *
 * ⛔ O rótulo não é redundante. A tarifa tem três casas (0,035) e o formatador
 * de moeda do documento usa duas — `brl(0.035)` imprime "R$ 0,04", que é
 * afirmar à Meta um preço 14% acima do que ela cobra. Por isso o texto é
 * escrito, com o "≈" que a terceira casa exige, em vez de derivado.
 */
export type TarifaMeta = { valor: number; rotulo: string };

/**
 * Alinhado em 29/09/2026 ao `meta-pricing.config.ts` do backend, que a Pauta
 * conferiu contra a tabela oficial em 28/09.
 *
 * ⚠️ ANTES ERA FAIXA ("R$ 0,03 a R$ 0,04"), sem origem declarada, e discordava
 * do outro documento da própria IPCOM: a proposta gerada por IA já imprimia
 * 0,035. Duas peças nossas citando preços diferentes da Meta para o mesmo
 * cliente é pior que um número redondo demais.
 *
 * ⚠️ DUAS TABELAS AINDA EXISTEM, aqui e no backend, e tabela duplicada volta a
 * divergir. O certo é o front LER do backend, como já faz com o Guia de Preços
 * ("o backend calcula, o front nunca calcula"). Está aberto como trabalho
 * separado — não dava para fazer na véspera da virada de 01/10.
 */
export const PRECOS_META: Record<'servico' | 'utilidade' | 'autenticacao' | 'marketing', TarifaMeta> = {
  /**
   * Resposta na janela de 24h, depois da virada de outubro. Mesmo preço da
   * utilidade, mas SEM desconto por volume — em volume alto sai mais caro que
   * ela, e é por isso que tem linha própria na tabela.
   */
  servico: { valor: 0.035, rotulo: '≈ R$ 0,035' },
  utilidade: { valor: 0.035, rotulo: '≈ R$ 0,035' },
  autenticacao: { valor: 0.035, rotulo: '≈ R$ 0,035' },
  marketing: { valor: 0.3217, rotulo: '≈ R$ 0,32' },
};

/**
 * A janela de 24h ainda é gratuita na data informada?
 *
 * Recebe a data em vez de olhar o relógio por dentro para poder ser testada nos
 * dois lados da virada — que é justamente o que não se consegue conferir olhando
 * o documento no dia.
 */
export function janela24hEhGratis(hoje: Date = new Date()): boolean {
  const virada = new Date(`${JANELA_24H_PASSA_A_COBRAR_EM}T00:00:00-03:00`);
  return hoje.getTime() < virada.getTime();
}

/**
 * A TABELA QUE O SERVIDOR PUBLICA, quando ela chega junto da proposta.
 *
 * ⛔ POR QUE NÃO SE BUSCA POR ENDPOINT. O backend #242 abriu
 * `GET /proposal-generation/meta-tarifas`, e ele exige perfil autenticado. Só
 * que quem desenha este documento é `ProposalPublicView`, a página que o
 * CLIENTE abre por link, SEM LOGIN — ela nunca teria sessão para chamar. O
 * caminho que fecha a duplicação é a tabela vir dentro do payload de
 * `GET /proposals/public/:code`, que o documento já recebe.
 *
 * Enquanto não vier, os valores acima continuam valendo. Eles são o fallback
 * declarado, não uma segunda verdade esquecida.
 */
export interface TabelaDaMeta {
  tarifas: Record<'servico' | 'utilidade' | 'autenticacao' | 'marketing', TarifaMeta>;
  cota?: { quantidade: number; porNumeroDeTelefone: boolean; resetaTodoMes: boolean };
  viradaDoServico?: string;
  ultimoDiaGratuito?: string;
  linhaDeReferencia: string;
}

const CATEGORIAS = ['servico', 'utilidade', 'autenticacao', 'marketing'] as const;

const tarifaValida = (t: any): boolean =>
  !!t && Number.isFinite(Number(t.valor)) && Number(t.valor) > 0 && String(t.rotulo ?? '').trim().length > 0;

/**
 * TUDO OU NADA, de propósito.
 *
 * Misturar campo do servidor com campo local produziria uma tabela que nunca
 * existiu em lugar nenhum — preço novo com referência velha, por exemplo, que é
 * pior que qualquer uma das duas inteiras. Se o que chegou não está completo,
 * vale o local, e ele é coerente consigo mesmo.
 */
export function tabelaDaMeta(doServidor?: any): TabelaDaMeta {
  const local: TabelaDaMeta = { tarifas: PRECOS_META, linhaDeReferencia: REFERENCIA_DOS_PRECOS };
  const t = doServidor?.tarifas;
  if (!t || !CATEGORIAS.every((c) => tarifaValida(t[c]))) return local;
  if (String(doServidor?.linhaDeReferencia ?? '').trim().length === 0) return local;
  return {
    tarifas: Object.fromEntries(
      CATEGORIAS.map((c) => [c, { valor: Number(t[c].valor), rotulo: String(t[c].rotulo) }]),
    ) as TabelaDaMeta['tarifas'],
    cota: doServidor.cota,
    viradaDoServico: doServidor.viradaDoServico,
    ultimoDiaGratuito: doServidor.ultimoDiaGratuito,
    linhaDeReferencia: String(doServidor.linhaDeReferencia),
  };
}

/**
 * A FRASE DA FONTE impressa sob a tabela da Meta, uma vez só.
 *
 * ⛔ As duas referências chegam em formatos diferentes. A local
 * (`REFERENCIA_DOS_PRECOS`) é só a fonte, sem ponto final. A do servidor (desde
 * o #159 o documento prefere ela) já termina com "Os valores são definidos pela
 * Meta e podem mudar sem aviso." A página acrescentava por conta própria ". São
 * definidos pela Meta e podem mudar sem aviso." e, com a do servidor, o cliente
 * lia "…sem aviso.. São definidos pela Meta e podem mudar sem aviso." (Pauta,
 * 01/10/2026, no ar desde a virada).
 *
 * Aqui o ponto final da referência sai antes de fechar a frase, e o aviso só
 * entra se a referência ainda não o diz.
 */
export function fraseDaFonte(linhaDeReferencia: string | null | undefined): string {
  const linha = String(linhaDeReferencia ?? '').trim().replace(/[.\s]+$/, '');
  const jaAvisa = /definidos pela Meta/i.test(linha);
  return jaAvisa
    ? `Fonte: ${linha}.`
    : `Fonte: ${linha}. São definidos pela Meta e podem mudar sem aviso.`;
}
